/**
 * Loads the image-with-text panel videos only as they near the screen. Desktop
 * plays the 1080p mp4; phones play the 720p variant of the HLS stream when the
 * browser plays HLS natively (Shopify keeps no 720p mp4 — see the comment in
 * sections/image-with-text.liquid), else the same mp4 as desktop.
 *
 * Autoplaying panels play while on screen and pause once scrolled well away, so
 * a phone never streams six videos nobody is looking at. A video the visitor
 * started themselves (autoplay off) is left alone.
 *
 * Included once per panel with `defer`, so it can run several times: the guard
 * keeps one observer, and init() only picks up videos it has not seen.
 */
(function () {
  "use strict";
  if (window.qsLazyVideo) return;
  window.qsLazyVideo = true;

  var phone = window.matchMedia("(max-width: 749px)");
  var observer = null;

  function play(video) {
    var p = video.play();
    if (p && p.catch) p.catch(function () { /* autoplay refused: controls remain */ });
  }

  var HLS_TYPE = "application/vnd.apple.mpegurl";
  var MAX_PHONE_HEIGHT = 720;

  /**
   * The URL of the tallest variant at or under 720p in an HLS master playlist,
   * resolved against the playlist's own URL, or null. Each variant line follows
   * an #EXT-X-STREAM-INF line carrying RESOLUTION=WxH.
   */
  function pickVariant(master, baseUrl) {
    var lines = master.split(/\r?\n/);
    var best = null;
    var bestHeight = 0;
    for (var i = 0; i < lines.length - 1; i++) {
      var m = /RESOLUTION=\d+x(\d+)/.exec(lines[i]);
      if (!m || lines[i].indexOf("#EXT-X-STREAM-INF") !== 0) continue;
      var h = parseInt(m[1], 10);
      var uri = lines[i + 1].trim();
      if (h <= MAX_PHONE_HEIGHT && h > bestHeight && uri && uri.charAt(0) !== "#") {
        best = new URL(uri, baseUrl).toString();
        bestHeight = h;
      }
    }
    return best;
  }

  function start(video, src) {
    video.src = src;
    if (video.getAttribute("data-autoplay") === "true") {
      video.muted = true;
      play(video);
    }
  }

  function load(video) {
    if (video.getAttribute("data-loaded")) return;
    video.setAttribute("data-loaded", "1");
    var mp4 = video.getAttribute("data-src-desktop");
    var hls = video.getAttribute("data-src-hls");
    if (!phone.matches || !hls || !video.canPlayType(HLS_TYPE) || !window.fetch) {
      start(video, mp4);
      return;
    }
    var masterUrl = new URL(hls, window.location.href).toString();
    fetch(masterUrl)
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (text) { start(video, pickVariant(text, masterUrl) || mp4); })
      .catch(function () { start(video, mp4); });
  }

  function onIntersect(entries) {
    for (var i = 0; i < entries.length; i++) {
      var video = entries[i].target;
      var auto = video.getAttribute("data-autoplay") === "true";
      if (entries[i].isIntersecting) {
        if (!video.getAttribute("data-loaded")) load(video);
        else if (auto && video.paused) play(video);
      } else if (auto && video.getAttribute("data-loaded") && !video.paused) {
        video.pause();
      }
    }
  }

  function init(root) {
    var videos = (root || document).querySelectorAll("video.qs-lazy-video:not([data-watched])");
    for (var i = 0; i < videos.length; i++) {
      var v = videos[i];
      v.setAttribute("data-watched", "1");
      if (!("IntersectionObserver" in window)) { load(v); continue; }
      if (!observer) observer = new IntersectionObserver(onIntersect, { rootMargin: "300px 0px" });
      observer.observe(v);
    }
  }

  try {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { init(); });
    else init();
    // Theme editor re-renders sections in place.
    document.addEventListener("shopify:section:load", function (e) { init(e.target); });
  } catch (e) {
    /* never break the page: videos simply stay on their poster */
  }
})();
